import { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Loader2, X, SkipForward, Pause, Ban, AlertTriangle, Calendar as CalendarIcon, MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { api } from "../lib/config";
import { toast } from "sonner";

const CANCEL_REASONS = [
  { key: "moving",        label: "Moving" },
  { key: "too_expensive", label: "Too expensive" },
  { key: "diy",           label: "Doing it myself" },
  { key: "other",         label: "Other" },
];

function addDaysIso(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ManageServiceMenu({ booking, onUpdated, variant = "icon" }) {
  const [open, setOpen] = useState(null); // "skip" | "pause" | "cancel"
  const isRecurring = !!booking?.frequency && booking.frequency !== "one_time";

  const trigger =
    variant === "row" ? (
      <Button variant="outline" className="rounded-full font-bold" data-testid={`manage-service-${booking.booking_id}`}>
        <MoreVertical className="w-4 h-4 mr-1.5" /> Manage service
      </Button>
    ) : (
      <Button variant="ghost" size="sm" className="rounded-full" data-testid={`manage-service-${booking.booking_id}`}>
        <MoreVertical className="w-4 h-4" />
      </Button>
    );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Manage service</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setOpen("skip")} data-testid={`manage-skip-${booking.booking_id}`}>
            <SkipForward className="w-4 h-4 mr-2 text-amber-600" /> Skip this visit
          </DropdownMenuItem>
          {isRecurring && (
            <DropdownMenuItem onClick={() => setOpen("pause")} data-testid={`manage-pause-${booking.booking_id}`}>
              <Pause className="w-4 h-4 mr-2 text-blue-600" /> Pause service
            </DropdownMenuItem>
          )}
          {isRecurring && (
            <DropdownMenuItem onClick={() => setOpen("cancel")} data-testid={`manage-cancel-${booking.booking_id}`} className="text-rose-700 focus:text-rose-700">
              <Ban className="w-4 h-4 mr-2" /> Cancel service
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SkipDialog open={open === "skip"} onClose={() => setOpen(null)} booking={booking} onUpdated={onUpdated} />
      <PauseDialog open={open === "pause"} onClose={() => setOpen(null)} booking={booking} onUpdated={onUpdated} />
      <CancelDialog open={open === "cancel"} onClose={() => setOpen(null)} booking={booking} onUpdated={onUpdated} />
    </>
  );
}

/* ---------------- Skip ---------------- */
function SkipDialog({ open, onClose, booking, onUpdated }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/subscription`, { action: "skip" });
      toast.success(data.next_booking_id ? "Visit skipped — next visit scheduled" : "Visit skipped");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip");
    } finally { setBusy(false); }
  };
  return (
    <Shell onClose={onClose} title="Skip this visit" tone="amber" icon={SkipForward} testid="skip-dialog">
      <p className="text-sm text-gray-700">Skip <span className="font-bold">{booking.preferred_date}</span> only — your recurring plan stays active.</p>
      <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 p-3 text-[12px] flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Skipping service may result in overgrown grass fees on the next visit.</span>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Keep visit</Button>
        <Button onClick={submit} disabled={busy} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white font-extrabold" data-testid="skip-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><SkipForward className="w-4 h-4 mr-1.5" /> Skip this visit</>}
        </Button>
      </div>
    </Shell>
  );
}

/* ---------------- Pause ---------------- */
function PauseDialog({ open, onClose, booking, onUpdated }) {
  const [choice, setChoice] = useState("2w");
  const [custom, setCustom] = useState(addDaysIso(30));
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const resume_date = choice === "2w" ? addDaysIso(14) : choice === "1m" ? addDaysIso(30) : custom;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/subscription`, { action: "pause", resume_date });
      toast.success(`Service paused — resumes ${resume_date}`);
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause");
    } finally { setBusy(false); }
  };
  return (
    <Shell onClose={onClose} title="Pause service" tone="blue" icon={Pause} testid="pause-dialog">
      <p className="text-sm text-gray-700">Your service will be paused until the resume date — no charges and no visits.</p>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { k: "2w", label: "2 weeks" },
          { k: "1m", label: "1 month" },
          { k: "custom", label: "Custom" },
        ].map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setChoice(opt.k)}
            data-testid={`pause-option-${opt.k}`}
            className={`px-3 py-3 rounded-2xl border-2 text-sm font-extrabold tracking-tight transition ${choice === opt.k ? "border-blue-500 bg-blue-50 text-blue-900" : "border-gray-200 hover:border-blue-300"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {choice === "custom" && (
        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-500">Resume date</Label>
          <Input
            type="date"
            value={custom}
            min={addDaysIso(1)}
            onChange={(e) => setCustom(e.target.value)}
            className="mt-1.5"
            data-testid="pause-custom-date"
          />
        </div>
      )}
      <div className="mt-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 p-3 text-[12px] flex items-start gap-2">
        <CalendarIcon className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Service resumes on <b>{resume_date}</b>. You can resume earlier from your dashboard.</span>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold" data-testid="pause-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Pause className="w-4 h-4 mr-1.5" /> Pause service</>}
        </Button>
      </div>
    </Shell>
  );
}

/* ---------------- Cancel ---------------- */
function CancelDialog({ open, onClose, booking, onUpdated }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    if (!confirming) { setConfirming(true); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/subscription`, {
        action: "cancel", reason, reason_note: note || null,
      });
      toast.success("Service cancelled. Sorry to see you go.");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); setConfirming(false); }
  };
  return (
    <Shell onClose={onClose} title="Cancel service" tone="rose" icon={Ban} testid="cancel-dialog">
      <p className="text-sm text-gray-700">We&apos;ll stop scheduling future visits. You can rebook anytime.</p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        {CANCEL_REASONS.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setReason(r.key)}
            data-testid={`cancel-reason-${r.key}`}
            className={`px-3 py-3 rounded-2xl border-2 text-sm font-extrabold tracking-tight transition ${reason === r.key ? "border-rose-500 bg-rose-50 text-rose-900" : "border-gray-200 hover:border-rose-300"}`}
          >
            {r.label}
          </button>
        ))}
      </div>
      {reason === "other" && (
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What's the reason?"
          rows={2}
          className="mt-3"
          data-testid="cancel-note"
        />
      )}
      {confirming && (
        <div className="mt-4 rounded-xl bg-rose-50 border-2 border-rose-200 text-rose-900 p-3 text-[12px] flex items-start gap-2" data-testid="cancel-confirm-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>This will stop all future recurring visits. Tap once more to confirm.</span>
        </div>
      )}
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => { setConfirming(false); onClose?.(); }} disabled={busy}>Keep service</Button>
        <Button onClick={submit} disabled={busy || !reason} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-extrabold" data-testid="cancel-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : confirming ? <><Ban className="w-4 h-4 mr-1.5" /> Confirm cancellation</> : <><Ban className="w-4 h-4 mr-1.5" /> Cancel service</>}
        </Button>
      </div>
    </Shell>
  );
}

/* ---------------- Shared shell ---------------- */
const TONES = {
  amber: "from-amber-600 to-amber-800",
  blue:  "from-blue-600 to-blue-800",
  rose:  "from-rose-600 to-rose-800",
};
function Shell({ onClose, title, tone, icon: Icon, testid, children }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/55 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid={testid}>
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`bg-gradient-to-br ${TONES[tone] || TONES.amber} text-white px-5 py-4 flex items-center gap-3`}>
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0 text-lg font-extrabold tracking-tight font-[Outfit] truncate">{title}</div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}
